import { Component, OnInit, OnDestroy, inject, output, ViewEncapsulation } from '@angular/core';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators, ValidatorFn, AbstractControl } from '@angular/forms';
import { CommonModule, TitleCasePipe } from '@angular/common';
import { BehaviorSubject, map, Observable, startWith } from 'rxjs';
// Material Imports
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatChipsModule } from '@angular/material/chips';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatRadioModule } from '@angular/material/radio';
import { MatDatepickerModule } from '@angular/material/datepicker';
// cdkTextareaAutosize: hace que el <textarea> crezca con el contenido.
import { TextFieldModule } from '@angular/cdk/text-field';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatTableModule } from '@angular/material/table';
// Interfaces
import { DialogData } from '@app/shared/models/custom-dialog.model';
// Servicio de carga/descarga de archivos (PDFs, imágenes) a S3.
import { UploadDownloadService } from '@app/shared/services/upload-download.service';
// Diálogo de gestión de archivos (cargar / previsualizar / descargar).
import { FileManagerDialogComponent } from '@shared/components/file-manager-dialog/file-manager-dialog.component';

/**
 * A partir de cuántas opciones un campo 'select' se convierte
 * automáticamente en un autocomplete con búsqueda. Los selects con
 * pocas opciones (ej. enums fijos como Activo/Inactivo) se quedan como
 * dropdown simple; los que vienen de catálogos suelen tener más.
 * Se puede forzar/desactivar por campo con `searchable`.
 */
const SEARCHABLE_SELECT_MIN_OPTIONS = 5;


import { RFID_TAG_LENGTH, rfidTagValidators } from '@app/shared/utils/rfid';
@Component({
  selector: 'app-custom-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatCheckboxModule,
    MatAutocompleteModule,
    MatRadioModule,
    MatDatepickerModule,
    MatIconModule,
    MatTooltipModule,
    MatTableModule,
    MatChipsModule,
    TextFieldModule,
    TitleCasePipe,
  ],
  templateUrl: './custom-dialog.component.html',
  styleUrl: './custom-dialog.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class CustomDialogComponent implements OnInit, OnDestroy {

  private readonly fb        = inject(FormBuilder);
  readonly dialogRef         = inject(MatDialogRef<CustomDialogComponent>);
  readonly data              = inject<DialogData>(MAT_DIALOG_DATA);
  private readonly uploadDownloadService = inject(UploadDownloadService);
  private readonly dialog    = inject(MatDialog);

  /** ¿El dispositivo es móvil/táctil? (cámara nativa vs preview en escritorio). */
  readonly isMobileDevice =
    typeof navigator !== 'undefined' &&
    (/Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent) ||
     (typeof matchMedia !== 'undefined' && matchMedia('(pointer: coarse)').matches));

  /** Reemplaza @Output() + EventEmitter con la nueva API output() */
  readonly actionClicked = output<string>();

  form!: FormGroup;
  filteredOptions:      Map<string, Observable<any[]>>       = new Map();
  multiFilteredOptions: Map<string, BehaviorSubject<any[]>>  = new Map();
  multiInputValues:     Map<string, string>                  = new Map();

  /** Keys de campos 'file' cuyo upload está en curso (para mostrar spinner). */
  uploadingFiles = new Set<string>();
  /** Nombre legible del último archivo subido por campo 'file'. */
  fileNames: Map<string, string> = new Map();

  /** Las etiquetas RFID siempre indican su longitud fija (24 caracteres). */
  fieldLabel(field: any): string {
    const label = String(field?.label ?? '');
    if (field?.type !== 'rfid' || /24\s*caracteres/i.test(label)) return label;
    return `${label} (${RFID_TAG_LENGTH} caracteres)`;
  }

  ngOnInit(): void {
    const group: Record<string, any> = {};

    this.data.fields.forEach(field => {
      if (field.type !== 'table' && field.type !== 'button') {
        const validators: ValidatorFn[] = field.required ? [Validators.required] : [];
        if (field.type === 'rfid') validators.push(...rfidTagValidators());
        // Select con búsqueda (autocomplete): el valor debe pertenecer a
        // la lista de opciones (no se permite texto libre).
        if (field.type === 'select' && this.isSearchableSelect(field)) {
          validators.push(this.inOptionsValidator(field));
        }
        let initialValue = field.value !== undefined ? field.value : '';
        if ((field.type === 'multiselect' || field.type === 'multiautocomplete') && !initialValue) {
          initialValue = [];
        }
        if (field.type === 'services') {
          // Normalizamos `services` a una lista de objetos
          // { service, subDireccion }, asegurando que ambos campos
          // existan aunque el registro de BD sólo traiga uno.
          const itemKey   = field.itemKey   ?? 'service';
          const detailKey = field.detailKey ?? 'subDireccion';
          if (!Array.isArray(initialValue)) {
            initialValue = [];
          } else {
            initialValue = initialValue
              .filter((row: any) => row && row[itemKey])
              .map((row: any) => ({
                [itemKey]:   row[itemKey],
                [detailKey]: row[detailKey] ?? '',
              }));
          }
        }
        if (field.type === 'contacts') {
          // `contacts` es una lista de objetos cuyas keys vienen de
          // field.columns. Aseguramos que cada fila tenga TODAS las
          // columnas declaradas, aunque el registro viejo no las traiga.
          const columns = field.columns ?? [];
          if (!Array.isArray(initialValue)) {
            initialValue = [];
          } else {
            initialValue = initialValue.map((row: any) => {
              const r: Record<string, any> = {};
              columns.forEach(col => { r[col.key] = row?.[col.key] ?? ''; });
              return r;
            });
          }
        }
        if (field.type === 'itemsList') {
          // Lista de objetos con N dropdowns (selectColumns) + cantidad.
          // Normalizamos cada fila a las keys declaradas.
          const selectCols = field.selectColumns ?? [];
          const qtyKey     = field.quantityKey ?? 'quantity';
          if (!Array.isArray(initialValue)) {
            initialValue = [];
          } else {
            initialValue = initialValue.map((row: any) => {
              const r: Record<string, any> = {};
              selectCols.forEach(col => { r[col.key] = row?.[col.key] ?? null; });
              r[qtyKey] = row?.[qtyKey] ?? null;
              return r;
            });
          }
        }
        if (field.type === 'file') {
          // El valor persistido es el path en S3 (string) o ''.
          if (typeof initialValue !== 'string') initialValue = '';
          if (initialValue) {
            // Mostramos solo el nombre del archivo (último segmento del path).
            this.fileNames.set(field.key, initialValue.split('/').pop() ?? 'archivo');
          }
        }
        if (field.type === 'camera') {
          // El valor persistido es un arreglo de keys S3 de las fotos.
          if (!Array.isArray(initialValue)) initialValue = [];
        }
        group[field.key] = [initialValue, validators];
      }
    });

    this.form = this.fb.group(group);
    this.setupDependencies();
    this.setupAutocompletes();
    this.setupSearchableSelects();
    this.setupMultiAutocompletes();
    this.setupUppercaseFields();
    this.setupPrefills();

    //disable
    this.data.fields.forEach(field => {
      if(field.disabled)this.form.get(field.key)?.disable();
    })
  }

  /**
   * Homologa los valores de los campos con `uppercase: true` a MAYÚSCULAS.
   * Aplica al valor inicial (en modo edición) y a cualquier cambio
   * posterior del usuario. Solo se aplica a inputs de tipo texto/rfid
   * para no afectar emails, números, ids resueltos, etc.
   */
  private setupUppercaseFields(): void {
    this.data.fields
      .filter(f => f.uppercase && (f.type === 'text' || f.type === 'rfid' || f.type === 'textarea'))
      .forEach(field => {
        const control = this.form.get(field.key);
        if (!control) return;

        // Uppercase del valor inicial (modo update)
        const initial = control.value;
        if (typeof initial === 'string' && initial !== initial.toUpperCase()) {
          control.setValue(initial.toUpperCase(), { emitEvent: false });
        }

        // Mientras el usuario escribe, fuerza mayúsculas sin disparar
        // valueChanges en cascada (emitEvent: false).
        control.valueChanges.subscribe(value => {
          if (typeof value !== 'string') return;
          const upper = value.toUpperCase();
          if (upper !== value) {
            control.setValue(upper, { emitEvent: false });
          }
        });
      });
  }

  // ─── Autollenado desde una fuente (prefill) ───────────────────────────────

  /**
   * Para cada campo con `prefill`, observa sus cambios de valor: cuando el
   * valor coincide con un objeto de la fuente (comparando contra `matchKey`),
   * copia las propiedades indicadas en `map` a los campos destino. Útil para,
   * p. ej., elegir una OC y autollenar el proveedor.
   */
  private setupPrefills(): void {
    this.data.fields
      .filter(f => f.prefill)
      .forEach(field => {
        const control = this.form.get(field.key);
        const cfg = field.prefill!;
        if (!control || typeof cfg.source !== 'function' || !cfg.map) return;

        control.valueChanges.subscribe(value => {
          if (value === '' || value === null || value === undefined) return;
          const src = (cfg.source() ?? []).find((o: any) => o?.[cfg.matchKey] === value);
          if (!src) return;
          Object.entries(cfg.map).forEach(([targetKey, srcProp]) => {
            const target = this.form.get(targetKey);
            if (!target) return;
            const next = src[srcProp as string];
            if (next === undefined) return;
            target.setValue(next);
            target.markAsDirty();
          });
        });
      });
  }

  // ─── Autocomplete ──────────────────────────────────────────────────────────

  private setupAutocompletes(): void {
    this.data.fields
      .filter(f => f.type === 'autocomplete')
      .forEach(field => {
        const control = this.form.get(field.key);
        if (!control) return;

        // Ver nota en setupSearchableSelects(): al seleccionar una opción
        // conservamos la vista filtrada para no destruir la <mat-option> en
        // pleno click (evita perder el valor al escribir y luego seleccionar).
        let lastSearch = '';

        const filtered$ = control.valueChanges.pipe(
          startWith(''),
          map(value => {
            const opts = field.options ?? [];
            const isSelection = opts.some((o: any) => o.value === value);
            if (isSelection) {
              if (!lastSearch) return opts;
              return opts.filter((opt: any) =>
                (opt.label ?? '').toLowerCase().includes(lastSearch)
              );
            }
            lastSearch = (typeof value === 'string' ? value : value?.label ?? '').toLowerCase();
            if (!lastSearch) return opts;
            return opts.filter((opt: any) =>
              (opt.label ?? '').toLowerCase().includes(lastSearch)
            );
          })
        );

        this.filteredOptions.set(field.key, filtered$);
      });
  }

  /** Muestra el label en el input en lugar del value (ID) */
  displayFn(options: any[]): (value: any) => string {
    return (value: any) => {
      const found = options?.find(opt => opt.value === value);
      return found ? found.label : '';
    };
  }

  // ─── Select con búsqueda (autocomplete) ──────────────────────────────────

  /**
   * ¿Este campo 'select' se renderiza como autocomplete con búsqueda?
   * - Si `searchable` está definido, manda ese valor.
   * - Si no, se activa cuando hay muchas opciones (típico de los selects
   *   alimentados por otros catálogos).
   */
  /**
   * Visibilidad condicional (`visibleWhen`): el campo sólo se muestra
   * cuando el campo padre tiene el valor esperado. `equals` acepta un
   * valor único o una lista. Sin `visibleWhen`, el campo siempre se ve.
   */
  isFieldVisible(field: any): boolean {
    const cond = field?.visibleWhen;
    if (!cond?.field) return true;
    const parentValue = this.form?.get(cond.field)?.value;
    const expected = Array.isArray(cond.equals) ? cond.equals : [cond.equals];
    return expected.includes(parentValue);
  }

  isSearchableSelect(field: any): boolean {
    if (typeof field?.searchable === 'boolean') return field.searchable;
    // Los selects dependientes (su lista viene de otro campo/catálogo)
    // se tratan siempre como buscables, para que la decisión sea estable
    // aunque sus opciones se llenen después (al elegir el padre).
    if (field?.dependsOn || field?.optionsMap) return true;
    return (field?.options?.length ?? 0) >= SEARCHABLE_SELECT_MIN_OPTIONS;
  }

  /**
   * Validador estricto: el valor debe pertenecer a la lista de opciones.
   * Permite vacío (eso lo cubre `required` por separado). Lee
   * `field.options` en vivo para soportar selects dependientes.
   */
  private inOptionsValidator(field: any): ValidatorFn {
    return (control: AbstractControl) => {
      const v = control.value;
      if (v === '' || v === null || v === undefined) return null;
      const ok = (field.options ?? []).some((o: any) => o.value === v);
      return ok ? null : { notInList: true };
    };
  }

  /**
   * Para cada select con búsqueda, arma el observable de opciones
   * filtradas según lo que el usuario escribe. Si el valor actual ya es
   * una opción válida (un id seleccionado) se muestran todas las opciones.
   */
  private setupSearchableSelects(): void {
    this.data.fields
      .filter(f => f.type === 'select' && this.isSearchableSelect(f))
      .forEach(field => {
        const control = this.form.get(field.key);
        if (!control) return;

        // Recordamos el último texto tecleado. Clave para el fix del bug:
        // al hacer click en una opción, valueChanges emite el `value` (id)
        // seleccionado. Si en ese momento re-expandiéramos la lista a "todas
        // las opciones", la <mat-option> que el usuario está clickeando se
        // destruye/recrea entre mousedown y mouseup y la selección se pierde
        // (por eso "al escribir y seleccionar no se quedaba", pero "al
        // scrollear sin escribir sí"). Manteniendo la MISMA vista filtrada
        // durante la selección, la opción no se destruye y el valor persiste.
        let lastSearch = '';

        const filtered$ = control.valueChanges.pipe(
          startWith(control.value ?? ''),
          map(value => {
            const opts = field.options ?? [];
            const isSelection = opts.some((o: any) => o.value === value);
            if (isSelection) {
              // No recalcular la búsqueda; conservar la vista filtrada actual.
              if (!lastSearch) return opts;
              return opts.filter((opt: any) =>
                (opt.label ?? '').toLowerCase().includes(lastSearch)
              );
            }
            lastSearch = (typeof value === 'string' ? value : '').toLowerCase();
            if (!lastSearch) return opts;
            return opts.filter((opt: any) =>
              (opt.label ?? '').toLowerCase().includes(lastSearch)
            );
          })
        );

        this.filteredOptions.set(field.key, filtered$);
      });
  }

  /** Opciones filtradas de un select con búsqueda (fallback a todas). */
  selectFilteredOptions(field: any): Observable<any[]> | undefined {
    return this.filteredOptions.get(field.key);
  }

  /** Usado por el multiautocomplete para que el input quede vacío al seleccionar */
  displayEmpty = (_: any): string => '';

  // ─── Multiautocomplete ────────────────────────────────────────────────────

  private setupMultiAutocompletes(): void {
    this.data.fields
      .filter(f => f.type === 'multiautocomplete')
      .forEach(field => {
        this.multiInputValues.set(field.key, '');
        this.multiFilteredOptions.set(
          field.key,
          new BehaviorSubject<any[]>(field.options ?? [])
        );
      });
  }

  onMultiInputChange(field: any, inputValue: string): void {
    this.multiInputValues.set(field.key, inputValue);
    this.refreshMultiFilter(field);
  }

  addMultiOption(field: any, optionValue: any): void {
    const control = this.form.get(field.key);
    if (!control) return;
    const current: any[] = control.value ?? [];
    if (!current.includes(optionValue)) {
      control.setValue([...current, optionValue]);
    }
    this.multiInputValues.set(field.key, '');
    this.refreshMultiFilter(field);
  }

  removeMultiOption(field: any, optionValue: any): void {
    const control = this.form.get(field.key);
    if (!control) return;
    control.setValue((control.value ?? []).filter((v: any) => v !== optionValue));
    this.refreshMultiFilter(field);
  }

  getOptionLabel(field: any, value: any): string {
    return field.options?.find((o: any) => o.value === value)?.label ?? value;
  }

  private refreshMultiFilter(field: any): void {
    const control    = this.form.get(field.key);
    const selected: any[] = control?.value ?? [];
    const search     = (this.multiInputValues.get(field.key) ?? '').toLowerCase();
    const filtered   = (field.options ?? []).filter((opt: any) =>
      !selected.includes(opt.value) &&
      opt.label.toLowerCase().includes(search)
    );
    this.multiFilteredOptions.get(field.key)?.next(filtered);
  }

  // ─── Multiselect ───────────────────────────────────────────────────────────

  isAllCheck(field: any): boolean {
    const control = this.form.get(field.key);
    if (!control || !field.options) return false;
    return (control.value || []).length === field.options.length;
  }

  isSomeChecked(field: any): boolean {
    const control = this.form.get(field.key);
    if (!control || !field.options) return false;
    const selected = (control.value || []).length;
    return selected > 0 && selected < field.options.length;
  }

  CheckAll(field: any): void {
    const control = this.form.get(field.key);
    if (!control || !field.options) return;
    if (this.isAllCheck(field)) {
      control.setValue([]);
    } else {
      control.setValue(field.options.map((opt: any) => opt.value));
    }
  }

  // ─── Services (servicios hospitalarios con sub dirección) ────────────────

  /** Helpers para resolver las claves del par item / detalle. */
  serviceItemKey(field: any): string   { return field.itemKey   ?? 'service'; }
  serviceDetailKey(field: any): string { return field.detailKey ?? 'subDireccion'; }
  serviceItemLabel(field: any): string { return field.itemLabel ?? 'Servicio'; }
  serviceDetailLabel(field: any): string { return field.detailLabel ?? 'Sub dirección'; }

  /** Lista actual de servicios del form (siempre array). */
  getServiceRows(field: any): any[] {
    const value = this.form.get(field.key)?.value;
    return Array.isArray(value) ? value : [];
  }

  /** Opciones disponibles excluyendo las ya seleccionadas. */
  availableServiceOptions(field: any): any[] {
    const itemKey  = this.serviceItemKey(field);
    const selected = this.getServiceRows(field).map(r => r?.[itemKey]);
    return (field.options ?? []).filter((opt: any) => !selected.includes(opt.value));
  }

  /** Agrega una nueva fila vacía (sin servicio seleccionado). */
  addServiceRow(field: any): void {
    const control = this.form.get(field.key);
    if (!control) return;
    const itemKey   = this.serviceItemKey(field);
    const detailKey = this.serviceDetailKey(field);
    const current   = this.getServiceRows(field);
    control.setValue([...current, { [itemKey]: null, [detailKey]: '' }]);
    control.markAsDirty();
  }

  removeServiceRow(field: any, index: number): void {
    const control = this.form.get(field.key);
    if (!control) return;
    const current = [...this.getServiceRows(field)];
    current.splice(index, 1);
    control.setValue(current);
    control.markAsDirty();
  }

  updateServiceItem(field: any, index: number, value: any): void {
    const control = this.form.get(field.key);
    if (!control) return;
    const itemKey = this.serviceItemKey(field);
    const current = [...this.getServiceRows(field)];
    if (!current[index]) return;
    current[index] = { ...current[index], [itemKey]: value };
    control.setValue(current);
    control.markAsDirty();
  }

  updateServiceDetail(field: any, index: number, value: any): void {
    const control = this.form.get(field.key);
    if (!control) return;
    const detailKey = this.serviceDetailKey(field);
    const current = [...this.getServiceRows(field)];
    if (!current[index]) return;
    current[index] = { ...current[index], [detailKey]: value };
    control.setValue(current);
    control.markAsDirty();
  }

  /**
   * Opciones del dropdown de una fila concreta: incluye las disponibles
   * y, si ya tiene un valor seleccionado, la opción correspondiente
   * (para que el select pueda mostrarla aunque no esté entre las
   * disponibles porque ya fue elegida).
   */
  serviceRowOptions(field: any, index: number): any[] {
    const itemKey = this.serviceItemKey(field);
    const rows = this.getServiceRows(field);
    const currentValue = rows[index]?.[itemKey];
    const available = this.availableServiceOptions(field);
    if (currentValue == null) return available;
    const currentOpt = (field.options ?? []).find((opt: any) => opt.value === currentValue);
    return currentOpt ? [currentOpt, ...available] : available;
  }

  // ─── Repeater genérico (contacts) ────────────────────────────────────────

  /** Lista actual de filas del repeater (siempre array). */
  getRepeaterRows(field: any): any[] {
    const value = this.form.get(field.key)?.value;
    return Array.isArray(value) ? value : [];
  }

  /** Tipo de input HTML para una columna del repeater. */
  repeaterInputType(col: any): string {
    return col?.type ?? 'text';
  }

  // ─── Campo de hora en 24 hrs (selectores HH : mm) ────────────────────────
  /** Opciones de horas 00..23 y minutos 00..59 (formato 24 hrs). */
  readonly hourOptions   = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
  readonly minuteOptions = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'));

  /** Parte de la hora guardada: idx 0 = horas, idx 1 = minutos. */
  timePart(field: any, idx: 0 | 1): string | null {
    const v = this.form.get(field.key)?.value;
    if (typeof v !== 'string' || !v.includes(':')) return null;
    return v.split(':')[idx] ?? null;
  }

  /** Setea horas o minutos manteniendo el formato HH:mm (24 hrs). */
  setTimePart(field: any, idx: 0 | 1, value: string): void {
    const control = this.form.get(field.key);
    if (!control) return;
    const cur = (typeof control.value === 'string' && control.value.includes(':'))
      ? control.value.split(':')
      : ['', ''];
    cur[idx] = value;
    const hh = (cur[0] || '00').padStart(2, '0');
    const mm = (cur[1] || '00').padStart(2, '0');
    control.setValue(`${hh}:${mm}`);
    control.markAsDirty();
  }

  /** Convierte el valor guardado (string ISO o Date) a Date para el datepicker. */
  toDate(value: any): Date | null {
    if (!value) return null;
    if (value instanceof Date) return value;
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }

  /** Agrega una nueva fila vacía con todas las columnas declaradas. */
  addRepeaterRow(field: any): void {
    const control = this.form.get(field.key);
    if (!control) return;
    const columns = field.columns ?? [];
    const emptyRow: Record<string, any> = {};
    columns.forEach((col: any) => { emptyRow[col.key] = ''; });
    control.setValue([...this.getRepeaterRows(field), emptyRow]);
    control.markAsDirty();
  }

  removeRepeaterRow(field: any, index: number): void {
    const control = this.form.get(field.key);
    if (!control) return;
    const current = [...this.getRepeaterRows(field)];
    current.splice(index, 1);
    control.setValue(current);
    control.markAsDirty();
  }

  updateRepeaterCell(field: any, index: number, colKey: string, value: any): void {
    const control = this.form.get(field.key);
    if (!control) return;
    const current = [...this.getRepeaterRows(field)];
    if (!current[index]) return;
    current[index] = { ...current[index], [colKey]: value };
    control.setValue(current);
    control.markAsDirty();
  }

  // ─── Carga de archivos (file) ─────────────────────────────────────────────

  /** ¿Hay un upload en curso para este campo? */
  isUploadingFile(field: any): boolean {
    return this.uploadingFiles.has(field.key);
  }

  /** Nombre legible del archivo cargado (o '' si no hay). */
  getFileName(field: any): string {
    return this.fileNames.get(field.key) ?? '';
  }

  /** ¿El campo ya tiene un archivo cargado? (booleano derivado del path) */
  hasFile(field: any): boolean {
    return !!this.form.get(field.key)?.value;
  }

  /** Dispara el input file oculto asociado al campo. */
  triggerFileInput(input: HTMLInputElement): void {
    input.click();
  }

  /** Maneja la selección de archivo: lo sube y guarda el path en el form. */
  async onFileSelected(field: any, event: Event): Promise<void> {
    const target = event.target as HTMLInputElement;
    const file   = target.files?.[0];
    if (!file) return;

    const control = this.form.get(field.key);
    if (!control) return;

    this.uploadingFiles.add(field.key);
    try {
      const uploaded = await this.uploadDownloadService.upload({
        path:  field.uploadPath ?? 'uploads',
        folio: field.uploadFolio ?? '',
        file,
      });
      // Persistimos el path en S3 (lo que después se puede re-presignar).
      control.setValue(uploaded.filename);
      control.markAsDirty();
      this.fileNames.set(field.key, file.name);
    } catch (err) {
      console.error('[CustomDialog] Error subiendo archivo:', err);
      // Dejamos el control como estaba; el usuario puede reintentar.
    } finally {
      this.uploadingFiles.delete(field.key);
      // Limpiamos el input para permitir re-seleccionar el mismo archivo.
      target.value = '';
    }
  }

  /** Quita el archivo cargado (resetea el path a ''). */
  clearFile(field: any): void {
    const control = this.form.get(field.key);
    if (!control) return;
    control.setValue('');
    control.markAsDirty();
    this.fileNames.delete(field.key);
  }

  /**
   * Abre el diálogo de gestión del documento (cargar / previsualizar /
   * descargar). Al cerrarse con "Guardar", actualiza el control con la key S3.
   */
  openFileManager(field: any): void {
    const control = this.form.get(field.key);
    if (!control) return;
    const ref = this.dialog.open(FileManagerDialogComponent, {
      data: {
        value:       control.value ?? '',
        label:       field.label,
        accept:      field.accept ?? '*/*',
        uploadPath:  field.uploadPath ?? 'uploads',
        uploadFolio: field.uploadFolio ?? '',
      },
      width: '680px', maxHeight: '90vh',
    });
    ref.afterClosed().subscribe((result: string | null) => {
      if (result === null || result === undefined) return; // cancelado
      control.setValue(result);
      control.markAsDirty();
      if (result) this.fileNames.set(field.key, result.split('/').pop() ?? 'archivo');
      else        this.fileNames.delete(field.key);
    });
  }

  // ─── Cámara + fotos a S3 (camera) ──────────────────────────────────────────

  /** Streams de cámara activos por campo (para attach al <video> y cleanup). */
  private cameraStreams: Map<string, MediaStream> = new Map();
  /** Cámaras de video disponibles (para alternar frontal / trasera). */
  cameraDevices: { deviceId: string; label: string }[] = [];
  /** Índice de la cámara activa por campo. */
  private currentCameraIndex = new Map<string, number>();

  /** Arreglo de keys S3 de las fotos del campo (siempre array). */
  getPhotos(field: any): string[] {
    const v = this.form.get(field.key)?.value;
    return Array.isArray(v) ? v : [];
  }

  /** Nombre legible (último segmento del key S3). */
  photoLabel(key: string): string {
    return (key ?? '').split('/').pop() || 'foto';
  }

  /** ¿Hay una cámara abierta para este campo? */
  isCameraActive(field: any): boolean {
    return this.cameraStreams.has(field.key);
  }

  /** Stream para enlazar a [srcObject] del <video>. */
  cameraStream(field: any): MediaStream | null {
    return this.cameraStreams.get(field.key) ?? null;
  }

  /** Abre la cámara del dispositivo (getUserMedia). */
  async startCamera(field: any, deviceId?: string): Promise<void> {
    if (this.cameraStreams.has(field.key)) return;
    try {
      const constraints: MediaStreamConstraints = {
        video: deviceId ? { deviceId: { exact: deviceId } } : { facingMode: 'environment' },
        audio: false,
      };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      this.cameraStreams.set(field.key, stream);
      await this.refreshCameraDevices(stream, field);
    } catch (err) {
      console.error('[CustomDialog] No se pudo abrir la cámara:', err);
    }
  }

  /** Lista las cámaras disponibles y fija el índice de la activa. */
  private async refreshCameraDevices(stream: MediaStream, field: any): Promise<void> {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      this.cameraDevices = devices
        .filter(d => d.kind === 'videoinput')
        .map((d, i) => ({ deviceId: d.deviceId, label: d.label || `Cámara ${i + 1}` }));
      const activeId = stream.getVideoTracks()[0]?.getSettings().deviceId;
      const idx = this.cameraDevices.findIndex(d => d.deviceId === activeId);
      this.currentCameraIndex.set(field.key, idx >= 0 ? idx : 0);
    } catch { /* enumerateDevices puede fallar; se ignora */ }
  }

  /** ¿Hay más de una cámara para alternar (frontal / trasera)? */
  hasMultipleCameras(): boolean {
    return this.cameraDevices.length > 1;
  }

  /** Etiqueta de la cámara activa. */
  activeCameraLabel(field: any): string {
    const idx = this.currentCameraIndex.get(field.key) ?? 0;
    return this.cameraDevices[idx]?.label ?? '';
  }

  /** Cambia a la siguiente cámara disponible (frontal / trasera / etc.). */
  async switchCamera(field: any): Promise<void> {
    if (this.cameraDevices.length < 2) return;
    const cur  = this.currentCameraIndex.get(field.key) ?? 0;
    const next = (cur + 1) % this.cameraDevices.length;
    this.stopCamera(field);
    this.currentCameraIndex.set(field.key, next);
    await this.startCamera(field, this.cameraDevices[next].deviceId);
  }

  /** Detiene la cámara y libera el stream. */
  stopCamera(field: any): void {
    const stream = this.cameraStreams.get(field.key);
    if (stream) {
      stream.getTracks().forEach(t => t.stop());
      this.cameraStreams.delete(field.key);
    }
  }

  /** Captura un frame del <video>, lo sube a S3 y guarda su key. */
  async capturePhoto(field: any, video: HTMLVideoElement): Promise<void> {
    if (!video || !video.videoWidth) return;
    const canvas = document.createElement('canvas');
    canvas.width  = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    const blob: Blob | null = await new Promise(resolve =>
      canvas.toBlob(b => resolve(b), 'image/jpeg', 0.85));
    if (!blob) return;

    const file = new File([blob], `foto-${Date.now()}.jpg`, { type: 'image/jpeg' });
    await this.uploadPhoto(field, file);
  }

  /** Maneja la selección/captura de imágenes desde el input file (móvil). */
  async onPhotosSelected(field: any, event: Event): Promise<void> {
    const target = event.target as HTMLInputElement;
    const files = target.files;
    if (!files?.length) return;
    for (let i = 0; i < files.length; i++) {
      await this.uploadPhoto(field, files.item(i)!);
    }
    target.value = '';
  }

  /** Sube una imagen a S3 y agrega su key al arreglo del campo. */
  private async uploadPhoto(field: any, file: File): Promise<void> {
    const control = this.form.get(field.key);
    if (!control) return;
    this.uploadingFiles.add(field.key);
    try {
      const uploaded = await this.uploadDownloadService.upload({
        path:  field.uploadPath ?? 'uploads',
        folio: field.uploadFolio ?? '',
        file,
      });
      control.setValue([...this.getPhotos(field), uploaded.filename]);
      control.markAsDirty();
    } catch (err) {
      console.error('[CustomDialog] Error subiendo foto:', err);
    } finally {
      this.uploadingFiles.delete(field.key);
    }
  }

  /** Abre una foto en el diálogo de gestión (previsualizar / descargar). */
  viewPhoto(field: any, index: number): void {
    const key = this.getPhotos(field)[index];
    if (!key) return;
    this.dialog.open(FileManagerDialogComponent, {
      data: { value: key, label: 'Foto', accept: 'image/*',
              uploadPath: field.uploadPath ?? 'uploads', uploadFolio: field.uploadFolio ?? '' },
      width: '680px', maxHeight: '90vh',
    }).afterClosed().subscribe((result: string | null) => {
      // Si el usuario reemplazó/quitó la foto, reflejarlo en el arreglo.
      if (result === null || result === undefined) return;
      const current = [...this.getPhotos(field)];
      if (result) current[index] = result;
      else current.splice(index, 1);
      this.form.get(field.key)?.setValue(current);
      this.form.get(field.key)?.markAsDirty();
    });
  }

  /** Quita una foto del arreglo (no la borra de S3). */
  removePhoto(field: any, index: number): void {
    const control = this.form.get(field.key);
    if (!control) return;
    const current = [...this.getPhotos(field)];
    current.splice(index, 1);
    control.setValue(current);
    control.markAsDirty();
  }

  /** Cierra todas las cámaras al destruir el dialog (evita fugas de stream). */
  ngOnDestroy(): void {
    this.cameraStreams.forEach(stream => stream.getTracks().forEach(t => t.stop()));
    this.cameraStreams.clear();
  }

  // ─── Repeater de items con selects + cantidad (itemsList) ─────────────────

  itemsListQtyKey(field: any): string   { return field.quantityKey ?? 'quantity'; }
  itemsListQtyLabel(field: any): string { return field.quantityLabel ?? 'Cantidad'; }

  /** Texto de búsqueda por celda-select del itemsList (clave: campo|fila|col). */
  private itemsSearch = new Map<string, string>();

  private itemCellKey(field: any, index: number, colKey: string): string {
    return `${field.key}|${index}|${colKey}`;
  }

  /** Label de la opción actualmente seleccionada en una celda-select. */
  itemCellLabel(col: any, row: any): string {
    const v = row?.[col.key];
    return (col.options ?? []).find((o: any) => o.value === v)?.label ?? '';
  }

  /** Texto a mostrar en el input: lo que se está buscando o el label elegido. */
  itemCellDisplay(field: any, col: any, index: number, row: any): string {
    const key = this.itemCellKey(field, index, col.key);
    if (this.itemsSearch.has(key)) return this.itemsSearch.get(key) ?? '';
    return this.itemCellLabel(col, row);
  }

  /** Opciones filtradas de una celda-select según el texto de búsqueda. */
  itemCellFilteredOptions(field: any, col: any, index: number): any[] {
    const term = (this.itemsSearch.get(this.itemCellKey(field, index, col.key)) ?? '').toLowerCase();
    const opts = col.options ?? [];
    if (!term) return opts;
    return opts.filter((o: any) => (o.label ?? '').toLowerCase().includes(term));
  }

  /** El usuario escribe en la celda-select: guardamos el texto de búsqueda. */
  onItemCellInput(field: any, index: number, colKey: string, value: string): void {
    this.itemsSearch.set(this.itemCellKey(field, index, colKey), value);
  }

  /** Se elige una opción: fija el valor y limpia el texto de búsqueda. */
  onItemCellSelected(field: any, index: number, colKey: string, value: any): void {
    this.updateItemsListCell(field, index, colKey, value);
    this.itemsSearch.delete(this.itemCellKey(field, index, colKey));
  }

  /** Al salir del input descartamos el texto no confirmado (solo valores de la lista). */
  onItemCellBlur(field: any, index: number, colKey: string): void {
    const key = this.itemCellKey(field, index, colKey);
    // Pequeño defer para permitir que (optionSelected) corra primero.
    setTimeout(() => this.itemsSearch.delete(key), 0);
  }

  /** Filas actuales del itemsList (siempre array). */
  getItemsListRows(field: any): any[] {
    const value = this.form.get(field.key)?.value;
    return Array.isArray(value) ? value : [];
  }

  /** Agrega una fila vacía con todas las columnas declaradas + cantidad. */
  addItemsListRow(field: any): void {
    const control = this.form.get(field.key);
    if (!control) return;
    const row: Record<string, any> = {};
    (field.selectColumns ?? []).forEach((col: any) => { row[col.key] = null; });
    row[this.itemsListQtyKey(field)] = null;
    control.setValue([...this.getItemsListRows(field), row]);
    control.markAsDirty();
  }

  removeItemsListRow(field: any, index: number): void {
    const control = this.form.get(field.key);
    if (!control) return;
    const current = [...this.getItemsListRows(field)];
    current.splice(index, 1);
    control.setValue(current);
    control.markAsDirty();
  }

  updateItemsListCell(field: any, index: number, colKey: string, value: any): void {
    const control = this.form.get(field.key);
    if (!control) return;
    const current = [...this.getItemsListRows(field)];
    if (!current[index]) return;
    current[index] = { ...current[index], [colKey]: value };
    control.setValue(current);
    control.markAsDirty();
  }

  /** Suma de las cantidades de todas las filas del itemsList. */
  itemsListSum(field: any): number {
    const qtyKey = this.itemsListQtyKey(field);
    return this.getItemsListRows(field).reduce((acc, row) => {
      const n = Number(row?.[qtyKey]);
      return acc + (isNaN(n) ? 0 : n);
    }, 0);
  }

  /** Tope máximo de la suma, tomado de otro campo del form (o null si no aplica). */
  itemsListMax(field: any): number | null {
    if (!field.maxFromField) return null;
    const raw = this.form.get(field.maxFromField)?.value;
    const n   = Number(raw);
    return raw === null || raw === '' || isNaN(n) ? null : n;
  }

  /** True si la suma de cantidades excede el tope máximo declarado. */
  itemsListExceeded(field: any): boolean {
    const max = this.itemsListMax(field);
    return max !== null && this.itemsListSum(field) > max;
  }

  /** True si ALGÚN campo itemsList excede su tope (bloquea el guardado). */
  hasItemsListError(): boolean {
    return this.data.fields.some(f => f.type === 'itemsList' && this.itemsListExceeded(f));
  }

  /** True si hay algún upload de archivo en curso (bloquea el guardado). */
  hasUploadInProgress(): boolean {
    return this.uploadingFiles.size > 0;
  }

  // ─── Dependencias padre-hijo ───────────────────────────────────────────────

  private setupDependencies(): void {
    this.data.fields.forEach(field => {
      if (field.dependsOn && field.optionsMap) {
        const parentControl = this.form.get(field.dependsOn);
        const childControl  = this.form.get(field.key);

        if (parentControl && childControl) {
          parentControl.valueChanges.subscribe(parentValue => {
            field.options = field.optionsMap![parentValue] || [];
            childControl.setValue(null);
            if (!parentValue) childControl.disable();
            else childControl.enable();
          });

          if (parentControl.value) {
            field.options = field.optionsMap[parentControl.value] || [];
          } else {
            field.options = [];
            childControl.disable();
          }
        }
      }
    });
  }

  // ─── Acciones del dialog ───────────────────────────────────────────────────

  onActionClick(content?: string): void {
    if (content) this.actionClicked.emit(content);
  }

  /** El guardado se bloquea si el form es inválido, hay un upload en curso
   *  o alguna lista de items excede su tope de cantidades. */
  get saveDisabled(): boolean {
    return this.form.invalid || this.hasUploadInProgress() || this.hasItemsListError();
  }

  onSave(): void {
    if (this.saveDisabled) return;
    if (this.form.valid) {
      const formValue = { ...this.form.value };

      this.data.fields.forEach(field => {
        if (field.type === 'date' && field.key === 'expiration') {
          const value = formValue[field.key];
          if (value) {
            const date  = new Date(value);
            const day   = String(date.getDate()).padStart(2, '0');
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const year  = date.getFullYear();
            formValue[field.key] = `${year}-${month}-${day}`;
          }
        }
      });

      this.dialogRef.close(formValue);
    }
  }

  onCancel(): void {
    this.dialogRef.close(null);
  }
}